import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, MoreVertical, Flag, Ban, Plus, Search, X, MessageCircle, Shield, AlertCircle } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * Format an ISO timestamp into a calm, low-noise relative label.
 *   < 1 min  → "now"
 *   < 60 min → "12m"
 *   < 24 h   → "3h"
 *   < 7 d    → "2d"
 *   older    → "Mar 4"
 *   older yr → "Mar 4, 2024"
 */
function formatRelativeTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60_000) return 'now';
    if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m`;
    if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h`;
    if (diffMs < 7 * 86_400_000) return `${Math.floor(diffMs / 86_400_000)}d`;
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString('en-US', sameYear
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
}

function formatExactTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleString('en-US', sameYear
      ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

/**
 * Connect Messaging App — FINAL LOCKED DIRECTIVE
 * 
 * Rules (non-negotiable):
 * - Anyone can message anyone (Members only)
 * - Only NON-CONTACTS are flaggable/blockable
 * - NO read receipts
 * - NO auto responses
 * - Handle-based messaging (no real names in list)
 * 
 * Entry points:
 * - Profile "Connect" button
 * - Notifications icon
 * 
 * Layout:
 * - Threads list (left/main on mobile)
 * - Chat view (on selection)
 * - Flag/Block actions for non-contacts
 */

export default function ConnectInbox() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchConversations();
  }, [user, navigate]);

  // Handle thread param in URL
  useEffect(() => {
    const threadId = searchParams.get('thread');
    if (threadId && conversations.length > 0) {
      const conv = conversations.find(c => c.thread_id === threadId);
      if (conv) {
        setSelectedConversation(conv);
      }
    }
  }, [searchParams, conversations]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      if (error.response?.status === 401) {
        navigate('/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/')} 
                className="p-2 -ml-2 rounded-lg hover:opacity-70"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
              </button>
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Connect</h1>
              </div>
            </div>
            <Button
              onClick={() => setShowNewChat(true)}
              variant="ghost"
              size="icon"
              className="w-9 h-9"
              style={{ color: 'var(--lk-sage)' }}
              data-testid="new-message-button"
            >
              <Plus className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Info notice */}
      <div className="max-w-2xl mx-auto px-4 py-2">
        <p className="text-xs text-center" style={{ color: 'var(--fy-text-disabled)' }}>
          Direct messages • Handle-based • No read receipts
        </p>
      </div>
      {/* Conversation list or chat view */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-4">
        {selectedConversation ? (
          <ChatView 
            conversation={selectedConversation}
            onBack={() => {
              setSelectedConversation(null);
              // Clear thread param from URL
              navigate('/inbox', { replace: true });
            }}
            onUpdate={fetchConversations}
          />
        ) : (
          <ConversationList 
            conversations={conversations}
            loading={loading}
            onSelect={(conv) => {
              setSelectedConversation(conv);
              navigate(`/inbox?thread=${conv.thread_id}`, { replace: true });
            }}
          />
        )}
      </main>

      {/* New Chat Modal */}
      {showNewChat && (
        <NewChatModal 
          onClose={() => setShowNewChat(false)}
          onCreated={(conv) => {
            setShowNewChat(false);
            setSelectedConversation(conv);
            fetchConversations();
          }}
        />
      )}
    </div>
  );
}

// ============ CONVERSATION LIST ============

function ConversationList({ conversations, loading, onSelect }) {
  if (loading) {
    return (
      <div className="space-y-3 py-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse flex gap-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
            <div className="w-12 h-12 rounded-full" style={{ backgroundColor: 'var(--fy-background-secondary)' }} />
            <div className="flex-1 space-y-2">
              <div className="h-4 rounded w-1/3" style={{ backgroundColor: 'var(--fy-background-secondary)' }} />
              <div className="h-3 rounded w-2/3" style={{ backgroundColor: 'var(--fy-background-secondary)' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="text-center py-16" data-testid="empty-conversations">
        <MessageCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--fy-text-disabled)' }} />
        <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--fy-text-primary)' }}>
          No messages yet
        </h2>
        <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
          Start a conversation from someone&apos;s profile, or tap + to message a contact.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2 py-4" data-testid="conversation-list">
      {conversations.map(conv => {
        const isUnread = (conv.unread_count || 0) > 0;
        return (
          <button
            key={conv.thread_id}
            onClick={() => onSelect(conv)}
            className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors hover:opacity-90"
            style={{
              backgroundColor: 'var(--fy-surface-muted)',
              border: `1px solid ${isUnread ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
            }}
            data-testid={`conversation-${conv.thread_id}`}
          >
            {/* Avatar */}
            <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                 style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
              {conv.other_user?.picture ? (
                <img src={conv.other_user.picture} alt="" className="w-12 h-12 object-cover" />
              ) : (
                <span className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                  {conv.other_user?.handle?.charAt(1) || '?'}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p
                  className={`truncate ${isUnread ? 'font-bold' : 'font-semibold'}`}
                  style={{ color: 'var(--fy-text-primary)' }}
                >
                  {conv.other_user?.handle || 'Unknown'}
                </p>
                {conv.is_contact && (
                  <span className="text-xs px-1.5 py-0.5 rounded flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage-muted)', color: 'var(--lk-sage-dark)' }}>
                    Contact
                  </span>
                )}
              </div>
              {conv.last_message && (
                <p
                  className="text-sm truncate"
                  style={{
                    color: isUnread ? 'var(--fy-text-primary)' : 'var(--fy-text-secondary)',
                    fontWeight: isUnread ? 500 : 400,
                  }}
                >
                  {conv.last_message.text}
                </p>
              )}
            </div>

            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              {conv.last_message_at && (
                <span className="text-xs" style={{ color: 'var(--fy-text-disabled)' }} data-testid={`conv-time-${conv.thread_id}`}>
                  {formatRelativeTime(conv.last_message_at)}
                </span>
              )}
              {isUnread && (
                <span
                  className="min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
                  data-testid={`conv-unread-${conv.thread_id}`}
                >
                  {conv.unread_count > 9 ? '9+' : conv.unread_count}
                </span>
              )}
              {conv.is_flagged && (
                <AlertCircle className="w-4 h-4" style={{ color: 'var(--lk-terra)' }} />
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ============ CHAT VIEW ============

function ChatView({ conversation, onBack, onUpdate }) {
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
  }, [conversation.thread_id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/messages/${conversation.thread_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setOtherUser(response.data.other_user);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/messages/${conversation.thread_id}/send`, null, {
        params: { text: newMessage.trim() },
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      fetchMessages();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFlag = async () => {
    const reason = prompt('Why are you flagging this conversation?');
    if (!reason) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/messages/${conversation.thread_id}/flag`, 
        { reason },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Conversation flagged for review');
      setShowMenu(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to flag conversation');
    }
  };

  const handleBlock = async () => {
    if (!confirm('Block this user? They won\'t be able to message you.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/messages/${conversation.thread_id}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User blocked');
      onBack();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    }
  };

  const handleReport = async () => {
    const reason = prompt('Why are you reporting this conversation?');
    if (!reason) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/messages/${conversation.thread_id}/report`, null, {
        params: { reason },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report submitted');
      setShowMenu(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    }
  };

  const chatName = otherUser?.handle || conversation.other_user?.handle || 'Unknown';
  const canFlagBlock = otherUser?.can_flag_block ?? conversation.can_flag_block ?? true;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]" data-testid="chat-view">
      {/* Chat header */}
      <div className="flex items-center justify-between py-3 mb-2" style={{ borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1 rounded hover:opacity-70" data-testid="chat-back-button">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden"
                 style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
              {(otherUser?.picture || conversation.other_user?.picture) ? (
                <img src={otherUser?.picture || conversation.other_user?.picture} alt="" className="w-8 h-8 object-cover" />
              ) : (
                <span className="text-sm font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                  {chatName.charAt(1) || '?'}
                </span>
              )}
            </div>
            <div>
              <p className="font-semibold text-sm" style={{ color: 'var(--fy-text-primary)' }}>{chatName}</p>
              {otherUser?.is_contact && (
                <p className="text-xs" style={{ color: 'var(--lk-sage)' }}>Contact</p>
              )}
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="relative">
          <button 
            onClick={() => setShowMenu(!showMenu)} 
            className="p-2 rounded hover:opacity-70"
            data-testid="chat-menu-button"
          >
            <MoreVertical className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-10 w-44 rounded-xl shadow-lg z-50" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <button
                onClick={handleReport}
                className="w-full flex items-center gap-2 p-3 text-left text-sm hover:opacity-70"
                style={{ color: 'var(--fy-text-primary)' }}
                data-testid="report-button"
              >
                <Shield className="w-4 h-4" />
                Report
              </button>
              {canFlagBlock && (
                <>
                  <button
                    onClick={handleFlag}
                    className="w-full flex items-center gap-2 p-3 text-left text-sm hover:opacity-70"
                    style={{ color: 'var(--lk-terra)', borderTop: '1px solid var(--fy-border-subtle)' }}
                    data-testid="flag-button"
                  >
                    <Flag className="w-4 h-4" />
                    Flag
                  </button>
                  <button
                    onClick={handleBlock}
                    className="w-full flex items-center gap-2 p-3 text-left text-sm hover:opacity-70"
                    style={{ color: 'var(--fy-accent-emphasis)', borderTop: '1px solid var(--fy-border-subtle)' }}
                    data-testid="block-button"
                  >
                    <Ban className="w-4 h-4" />
                    Block
                  </button>
                </>
              )}
              {!canFlagBlock && (
                <p className="p-3 text-xs" style={{ color: 'var(--fy-text-disabled)', borderTop: '1px solid var(--fy-border-subtle)' }}>
                  Flag/Block not available for contacts
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 py-2">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>
              Start the conversation. Be kind.
            </p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <MessageBubble 
              key={msg.message_id || idx} 
              message={msg} 
              isOwn={msg.sender_id === user?.user_id || msg.is_own} 
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex items-center gap-2 py-3" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
        <Input
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Message..."
          className="flex-1 rounded-full"
          style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
          data-testid="message-input"
        />
        <Button
          type="submit"
          disabled={!newMessage.trim() || sending}
          size="icon"
          className="w-10 h-10 rounded-full"
          style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
          data-testid="send-button"
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}

function MessageBubble({ message, isOwn }) {
  const stamp = formatExactTime(message.created_at);
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-[75%]`}>
        <div
          className={`rounded-2xl px-4 py-2 ${isOwn ? 'rounded-br-md' : 'rounded-bl-md'}`}
          style={{
            backgroundColor: isOwn ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
            color: isOwn ? 'white' : 'var(--fy-text-primary)'
          }}
        >
          {!isOwn && message.sender_handle && (
            <p className="text-xs font-medium mb-1" style={{ opacity: 0.8 }}>{message.sender_handle}</p>
          )}
          <p className="text-sm whitespace-pre-wrap break-words">{message.content || message.text}</p>
          {message.image_url && (
            <img src={message.image_url} alt="" className="mt-2 rounded-lg max-w-full" />
          )}
        </div>
        {stamp && (
          <span
            className="text-[10px] mt-1 px-1"
            style={{ color: 'var(--fy-text-disabled)' }}
            data-testid="message-timestamp"
          >
            {stamp}
          </span>
        )}
      </div>
    </div>
  );
}

// ============ NEW CHAT MODAL ============

function NewChatModal({ onClose, onCreated }) {
  const [contacts, setContacts] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get contacts for easy selection
      const response = await axios.get(`${API}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedUser) return;
    
    try {
      setCreating(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/messages/conversations/create`, {
        participant_ids: [selectedUser.user_id],
        is_group: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onCreated(response.data.conversation);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  };

  const filteredContacts = contacts.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="w-full max-w-md max-h-[80vh] rounded-t-3xl sm:rounded-3xl overflow-hidden" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--fy-border-subtle)' }}>
          <h2 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>New Message</h2>
          <button onClick={onClose} className="p-2 rounded hover:opacity-70" data-testid="close-modal-button">
            <X className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
          </button>
        </div>

        {/* Info */}
        <div className="px-4 py-2">
          <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
            Select a contact to message, or use the Connect button on any profile.
          </p>
        </div>

        {/* Search */}
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search contacts..."
              className="pl-10 rounded-full"
              style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
              data-testid="search-contacts-input"
            />
          </div>
        </div>

        {/* Contacts list */}
        <div className="overflow-y-auto px-4 pb-4" style={{ maxHeight: '40vh' }}>
          {loading ? (
            <p className="text-center py-4 text-sm" style={{ color: 'var(--fy-text-disabled)' }}>Loading contacts...</p>
          ) : filteredContacts.length === 0 ? (
            <p className="text-center py-4 text-sm" style={{ color: 'var(--fy-text-disabled)' }}>
              {contacts.length === 0 ? 'No contacts yet. Add people as contacts or message them from their profile.' : 'No matches found'}
            </p>
          ) : (
            <div className="space-y-2">
              {filteredContacts.map(contact => {
                const isSelected = selectedUser?.user_id === contact.user_id;
                return (
                  <button
                    key={contact.user_id}
                    onClick={() => setSelectedUser(contact)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors"
                    style={{ 
                      backgroundColor: isSelected ? 'var(--lk-sage-muted)' : 'var(--fy-surface-muted)',
                      border: `1px solid ${isSelected ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`
                    }}
                    data-testid={`contact-${contact.user_id}`}
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
                      {contact.picture ? (
                        <img src={contact.picture} alt="" className="w-10 h-10 object-cover" />
                      ) : (
                        <span className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                          {contact.name?.charAt(0) || '@'}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                        @{contact.username || 'user'}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                        {contact.name}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--lk-sage)' }}>
                        <span className="text-xs text-white">✓</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Create button */}
        <div className="p-4" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
          <Button
            onClick={handleCreate}
            disabled={!selectedUser || creating}
            className="w-full h-12 rounded-full font-bold"
            style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
            data-testid="start-conversation-button"
          >
            {creating ? 'Starting...' : 'Start Conversation'}
          </Button>
        </div>
      </div>
    </div>
  );
}
